import promiseFinally from 'promise-finally';
import pTimeout from 'p-timeout';
import eventHelper from './eventHelper';
import VideoElementFacadeFactory from './video_element/index';
import isVideoFrameBlackDefault from './isVideoFrameBlack';
import defaultEnv from './env';

// eslint-disable-next-line no-unused-vars
const typeVideoElementFacade = VideoElementFacadeFactory();

/**
 * Wait for the video to be playing
 * @param {Object} params
 *  @property {typeVideoElementFacade} videoElementFacade
 *  @property {Number} maxWaitTime
 *  @property {Boolean} isBufferedStream - whether or not the video is a buffered stream (has a video track, we're subscribing to video)
 *  @property {Object} deps - dependencies for the function, used for unit testing
 *
 * @returns Promise<Boolean> true if we think the video is playing, false if we timeout
 */
function waitForVideoToBePlaying({
  defaultVideoElementFacade,
  maxWaitTime = 5000,
  isBufferedStream = false,
  deps = {},
}) {
  const env = deps.env || defaultEnv;
  const videoElementFacade = deps.videoElementFacade || defaultVideoElementFacade;
  const isVideoFrameBlack = deps.isVideoFrameBlack || isVideoFrameBlackDefault;
  const videoElementFacadeEvents = deps.videoElementFacadeEvents || eventHelper(videoElementFacade);
  let timeoutReturnValue = false;
  let requestCallbackId;

  const promise = new Promise((resolve) => {
    // eslint-disable-next-line consistent-return
    const onVideoPlaybackHandler = (videoPlaybackEvent) => {
      // When the video is playing, we resolve with the event at timeout.
      timeoutReturnValue = videoPlaybackEvent;

      // Clean up event listeners - we only needed to listen until the first playback event
      videoElementFacadeEvents.removeAll();

      if (!isBufferedStream || !env.isChromium) {
        // If there is no video and we're not subscribed to video, or if we're using a non-Chromium browser,
        // resolve immediately because the video frame callback will never be called or because the bug is not present on non-Chromium browsers.
        return resolve(videoPlaybackEvent);
      }

      const videoFrameCallback = () => {
        // During an AMR transition or any new stream bind, we check that a real video frame is displayed for Chromium browsers
        // before switching streams to eliminate the screen flicker. See VIDCS-3570.
        const hasBlackVideoFrame = isVideoFrameBlack(videoElementFacade);

        if (!hasBlackVideoFrame) {
          // If we have a real video frame, we proceed.
          return resolve(videoPlaybackEvent);
        }

        // If we haven't seen a non-black video frame, we re-register the callback.
        requestCallbackId = videoElementFacade.requestVideoFrameCallback(
          videoFrameCallback
        );
        return requestCallbackId;
      };

      requestCallbackId = videoElementFacade.requestVideoFrameCallback(videoFrameCallback);
    };
    videoElementFacadeEvents.on('timeupdate', onVideoPlaybackHandler);
    videoElementFacadeEvents.on('loadedmetadata', onVideoPlaybackHandler);
  });

  return promiseFinally(
    pTimeout(promise, maxWaitTime, () => timeoutReturnValue),
    () => {
      videoElementFacadeEvents.removeAll();
      if (requestCallbackId) {
        videoElementFacade.cancelVideoFrameCallback(requestCallbackId);
      }
    }
  );
}

export default waitForVideoToBePlaying;
